import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControlLabel,
  Switch,
  Alert,
  CircularProgress,
  Grid,
  Box,
  Typography,
  Input
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import dayjs from 'dayjs';
import axios from 'axios';

const StoryDialog = ({ open, onClose, story, onSave }) => {
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    excerpt: '',
    published_date: dayjs(),
    is_published: true,
    is_featured: false,
    image_alt: ''
  });
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (story) {
      setFormData({
        title: story.title || '',
        content: story.content || '',
        excerpt: story.excerpt || '',
        published_date: story.published_date ? dayjs(story.published_date) : dayjs(),
        is_published: story.is_published !== undefined ? story.is_published : true,
        is_featured: story.is_featured !== undefined ? story.is_featured : false,
        image_alt: story.image_alt || ''
      });
      setImagePreview(story.image_url ? `${import.meta.env.VITE_API_URL}${story.image_url}` : null);
    } else {
      setFormData({
        title: '',
        content: '',
        excerpt: '',
        published_date: dayjs(),
        is_published: true,
        is_featured: false,
        image_alt: ''
      });
      setImagePreview(null);
    }
    setSelectedImage(null);
    setError('');
  }, [story, open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const submitData = new FormData();
      submitData.append('title', formData.title);
      submitData.append('content', formData.content);
      submitData.append('excerpt', formData.excerpt);
      submitData.append('published_date', formData.published_date.format('YYYY-MM-DD'));
      submitData.append('is_published', formData.is_published);
      submitData.append('is_featured', formData.is_featured);
      submitData.append('image_alt', formData.image_alt);

      if (selectedImage) {
        submitData.append('image', selectedImage);
      }

      if (story) {
        // Update existing story
        await axios.put(`/api/stories/admin/${story.id}`, submitData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
      } else {
        // Create new story
        await axios.post('/api/stories/admin', submitData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
      }
      
      onSave();
      onClose();
    } catch (error) {
      console.error('Error saving story:', error);
      setError(error.response?.data?.message || 'Failed to save story');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleContentChange = (content) => {
    setFormData(prev => ({
      ...prev,
      content: content
    }));
  };

  const handleDateChange = (date) => {
    setFormData(prev => ({
      ...prev,
      published_date: date
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedImage(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const quillModules = {
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      ['blockquote', 'code-block'],
      ['link'],
      ['clean']
    ],
  };

  const quillFormats = [
    'header', 'bold', 'italic', 'underline', 'strike',
    'list', 'bullet', 'blockquote', 'code-block', 'link'
  ];

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          {story ? 'Edit Story' : 'Add New Story'}
        </DialogTitle>
        
        <form onSubmit={handleSubmit}>
          <DialogContent>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  autoFocus
                  label="Story Title"
                  type="text"
                  fullWidth
                  variant="outlined"
                  value={formData.title}
                  onChange={handleInputChange('title')}
                  required
                  disabled={loading}
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  label="Excerpt (Short description)"
                  type="text"
                  fullWidth
                  variant="outlined"
                  multiline
                  rows={2}
                  value={formData.excerpt}
                  onChange={handleInputChange('excerpt')}
                  disabled={loading}
                  helperText="Optional brief summary of the story"
                />
              </Grid>
              
              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom>
                  Story Content
                </Typography>
                <Box sx={{ mb: 2 }}>
                  <ReactQuill
                    theme="snow"
                    value={formData.content}
                    onChange={handleContentChange}
                    modules={quillModules}
                    formats={quillFormats}
                    placeholder="Write your story here..."
                    style={{ height: '200px', marginBottom: '50px' }}
                  />
                </Box>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <DatePicker
                  label="Published Date"
                  value={formData.published_date}
                  onChange={handleDateChange}
                  disabled={loading}
                  slotProps={{
                    textField: {
                      fullWidth: true
                    }
                  }}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <Box display="flex" flexDirection="column" gap={1}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.is_published}
                        onChange={handleInputChange('is_published')}
                        disabled={loading}
                      />
                    }
                    label="Published (visible to public)"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.is_featured}
                        onChange={handleInputChange('is_featured')}
                        disabled={loading}
                      />
                    }
                    label="Featured story"
                  />
                </Box>
              </Grid>
              
              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom>
                  Story Image
                </Typography>
                <Input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  disabled={loading}
                  sx={{ mb: 2, width: '100%' }}
                />
                
                {imagePreview && (
                  <Box sx={{ mb: 2 }}>
                    <img
                      src={imagePreview}
                      alt="Preview"
                      style={{
                        maxWidth: '100%',
                        maxHeight: '200px',
                        objectFit: 'cover',
                        borderRadius: '4px'
                      }}
                    />
                  </Box>
                )}
                
                <TextField
                  label="Image Alt Text"
                  type="text"
                  fullWidth
                  variant="outlined"
                  value={formData.image_alt}
                  onChange={handleInputChange('image_alt')}
                  disabled={loading}
                  helperText="Describe the image for accessibility"
                />
              </Grid>
            </Grid>
          </DialogContent>
          
          <DialogActions>
            <Button onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button 
              type="submit" 
              variant="contained" 
              disabled={loading}
              startIcon={loading && <CircularProgress size={20} />}
            >
              {loading ? 'Saving...' : (story ? 'Update' : 'Create')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </LocalizationProvider>
  );
};

export default StoryDialog;